import { AnimatePresence, motion } from 'framer-motion';
import { ExternalLink, X } from 'lucide-react';
import qrcode from 'qrcode-generator';
import { useEffect, useMemo, useRef } from 'react';
import { create } from 'zustand';
import { usePreviewImage } from '../hooks/usePreviewImage';
import { useSpatialNav } from '../hooks/useSpatialNav';
import type { NewsItem } from '../hooks/useWidgetsData';

/** Which story (if any) is open in the pop-up. */
export const useArticle = create<{ article: NewsItem | null; open: (a: NewsItem) => void; close: () => void }>()((set) => ({
  article: null,
  open: (article) => set({ article }),
  close: () => set({ article: null }),
}));

export function timeAgo(iso: string | null | undefined): string {
  if (!iso) return '';
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  return hours < 24 ? `${hours} h ago` : new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

/**
 * Story pop-up: picture, headline and the publisher's summary, with a link to the full
 * article and a QR code to carry on reading on a phone (TVs rarely have a good browser).
 * We don't embed or copy the article itself – most publishers block framing, and their
 * full text is theirs.
 */
export function ArticleModal() {
  const { article, close } = useArticle();
  const image = usePreviewImage(article?.image, article?.link);
  const dialog = useRef<HTMLElement>(null);
  useSpatialNav(dialog, article !== null);

  useEffect(() => {
    if (!article) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'GoBack' || e.key === 'BrowserBack') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [article, close]);

  const qr = useMemo(() => {
    if (!article?.link) return null;
    const q = qrcode(0, 'M');
    q.addData(article.link);
    q.make();
    return q.createDataURL(4, 2);
  }, [article?.link]);

  return (
    <AnimatePresence>
      {article && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={close}
        >
          <motion.article
            ref={dialog}
            role="dialog"
            aria-modal="true"
            aria-label={article.title}
            className="relative max-h-full w-full max-w-3xl overflow-y-auto rounded-2xl bg-[var(--surface-strong)] text-[var(--fg)] shadow-2xl"
            initial={{ y: 24, scale: 0.98 }}
            animate={{ y: 0, scale: 1 }}
            exit={{ y: 24, scale: 0.98 }}
            onClick={(e) => e.stopPropagation()}
          >
            {image && (
              <img src={image} alt="" className="aspect-video w-full rounded-t-2xl object-cover" onError={(e) => (e.currentTarget.style.display = 'none')} />
            )}
            <div className="space-y-4 p-6 md:p-8">
              <div className="text-xs font-semibold uppercase tracking-wider text-[var(--accent)]">
                {article.source}
                {article.published && <span className="muted font-normal normal-case"> · {timeAgo(article.published)}</span>}
              </div>
              <h2 className="text-2xl font-semibold leading-snug md:text-3xl">{article.title}</h2>
              {article.summary && <p className="text-lg leading-relaxed opacity-90">{article.summary}</p>}
              <div className="flex flex-wrap items-center gap-5 pt-2">
                {article.link && (
                  <a
                    href={article.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    autoFocus
                    className="inline-flex items-center gap-2 rounded-full bg-[var(--accent)] px-5 py-2.5 font-medium text-black"
                  >
                    Read full article <ExternalLink className="h-4 w-4" aria-hidden />
                  </a>
                )}
                {qr && (
                  <div className="flex items-center gap-3">
                    <img src={qr} alt="QR code for the article" className="h-24 w-24 rounded-lg bg-white p-1" />
                    <span className="muted max-w-[10rem] text-xs">Scan to read on your phone</span>
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={close}
              className="absolute right-4 top-4 rounded-full bg-black/50 p-2 text-white hover:bg-black/70"
              aria-label="Close article"
            >
              <X className="h-5 w-5" aria-hidden />
            </button>
          </motion.article>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
